/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Standard CRUD Operations Hook
 *
 * Provides consistent CRUD operation patterns across all domain pages
 * including error handling, success messages, and navigation.
 */

"use client";

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useNavigationWithProgress } from "@/hooks/utilities/use-navigation-with-progress";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { logger } from "@/lib/utils/logger";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useTransition } from "react";
import { toast } from "sonner";

interface StandardCrudConfig {
  domain: string;
  apiBasePath: string;
  createRoute?: string;
  editRoute?: (id: string) => string;
  viewRoute?: (id: string) => string;
  entityLabel: string;
  entityLabelPlural: string;
}

interface CrudOperations {
  handleCreate: () => void;
  handleEdit: (id: string) => void;
  handleView: (id: string) => void;
  handleDelete: (id: string, invalidate: () => void) => Promise<void>;
  isNavigating: boolean;
}

export function useStandardCrudOperations(
  config: StandardCrudConfig,
): CrudOperations {
  const { push } = useNavigationWithProgress();
  const searchParams = useSearchParams();
  const { getToken } = useAuthSafe();
  const [isNavigating, startNavigation] = useTransition();

  const {
    domain,
    apiBasePath,
    createRoute = `/app/${domain}/new`,
    editRoute = (id: string) => `/app/${domain}/${id}/edit`,
    viewRoute = (id: string) => `/app/${domain}/${id}`,
    entityLabel,
  } = config;

  // URL parameter handling for success messages
  useEffect(() => {
    try {
      const mappings: Record<"created" | "updated" | "deleted", string> = {
        created: `${entityLabel} created`,
        updated: `${entityLabel} updated`,
        deleted: `${entityLabel} deleted`,
      };
      let changed = false;

      for (const key of ["created", "updated", "deleted"] as const) {
        if (searchParams.get(key) === "1") {
          toast.success(mappings[key]);
          changed = true;
        }
      }

      if (changed) {
        const newSearchParams = new URLSearchParams(searchParams);
        for (const key of ["created", "updated", "deleted"] as const) {
          newSearchParams.delete(key);
        }
        const newUrl = `${window.location.pathname}${newSearchParams.toString() ? "?" + newSearchParams.toString() : ""}`;
        window.history.replaceState(null, "", newUrl);
      }
    } catch (error) {
      logger.error("Failed to update URL with search params:", error);
    }
  }, [searchParams, entityLabel]);

  const handleCreate = useCallback(() => {
    if (isNavigating) return;
    startNavigation(() => {
      push(createRoute);
    });
  }, [isNavigating, startNavigation, push, createRoute]);

  const handleEdit = useCallback(
    (id: string) => {
      if (isNavigating) return;
      // Dispatch event BEFORE transition to ensure progress bar shows immediately
      window.dispatchEvent(new Event("navigationstart"));
      startNavigation(() => {
        push(editRoute(id));
      });
    },
    [isNavigating, startNavigation, push, editRoute],
  );

  const handleView = useCallback(
    (id: string) => {
      if (isNavigating) return;
      // Dispatch event BEFORE transition to ensure progress bar shows immediately
      window.dispatchEvent(new Event("navigationstart"));
      startNavigation(() => {
        push(viewRoute(id));
      });
    },
    [isNavigating, startNavigation, push, viewRoute],
  );

  const handleDelete = useCallback(
    async (id: string, invalidate: () => void) => {
      try {
        const api = createApiClient({
          getToken,
          getOrganizationId: () => undefined,
          autoRetry429: true,
        });

        const result = await api.del(`${apiBasePath}/${id}`);

        if (isApiFailure(result)) {
          toast.error("Error", {
            description:
              result.message || `Failed to delete ${entityLabel.toLowerCase()}`,
          });
          throw new Error(result.message);
        }

        toast.success("Success", {
          description: `${entityLabel} deleted successfully`,
        });

        invalidate();
      } catch (error) {
        logger.error(`Failed to delete ${entityLabel.toLowerCase()}:`, error);
        toast.error("Error", {
          description: `Failed to delete ${entityLabel.toLowerCase()}`,
        });
      }
    },
    [getToken, apiBasePath, entityLabel],
  );

  return {
    handleCreate,
    handleEdit,
    handleView,
    handleDelete,
    isNavigating,
  };
}
